import { useRef } from "react";
import { Box, Chip, Stack, Typography, alpha } from "@mui/material";
import { darken } from "@mui/material/styles";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import Person2 from "@mui/icons-material/Person2";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { DateTime } from "luxon";

import { BaseModalComponent } from "../../../../../../../common/BaseModalComponent";
import { EventJobModal } from "../single-event/EventJobModal";
import { getStatusPalette } from "../../helpers/normalizedColorsCalendar";

export const GroupedEventCard = ({ event }) => {
  const triggerRef = useRef(null);

  const { start, end, extendedProps, title } = event;
  const { jobId, status, quote, color } = extendedProps;

  const baseColor = color || "#6b7280";
  const barColor = darken(baseColor, 0.35);
  const hoverBg = alpha(baseColor, 0.25);
  const border = alpha(baseColor, 0.4);
  const textColor = "#111";

  const startTime = DateTime.fromJSDate(start).toFormat("h:mm a");
  const endTime = end ? DateTime.fromJSDate(end).toFormat("h:mm a") : null;

  const palette = getStatusPalette(status);

  return (
    <>
      <Box
        onClick={() => triggerRef.current?.click?.()}
        sx={{
          position: "relative",
          p: 2,
          pb: 2.4,
          mb: 2,
          pl: 3,
          borderRadius: 2,
          bgcolor: "var(--fx-surface-base, #FFFFFF)",
          border: `1px solid ${border}`,
          color: textColor,
          transition: "all 0.15s ease",
          cursor: "pointer",

          display: "flex",
          flexDirection: "column",
          gap: 1.4, 

          "&::before": {
            content: '""',
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: 6,
            bgcolor: barColor,
            borderTopLeftRadius: "8px",
            borderBottomLeftRadius: "8px",
          },

          "&:hover": {
            bgcolor: hoverBg,
            boxShadow: `0 0 0 1px ${border}`,
            transform: "scale(1.01)",
          },
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          sx={{ gap: 1.2 }}
        >
          <Chip
            label={`#${jobId}`}
            size="small"
            sx={{
              bgcolor: alpha("#374151", 0.08),
              border: `1px solid ${alpha("var(--fx-surface-textsecondary, #64748B)", 0.2)}`,
              color: "var(--fx-surface-textsecondary, #64748B)",
              fontWeight: 700,
              fontSize: "0.7rem",
              height: 22,
              "& .MuiChip-label": { px: 1 },
            }}
          />

          <Chip
            icon={<FiberManualRecordIcon sx={{ fontSize: 8 }} />}
            label={status}
            size="small"
            sx={{
              bgcolor: palette.color,
              color: "#fff",
              fontWeight: 600,
              fontSize: "0.65rem",
              textTransform: "capitalize",
              height: 22,
              "& .MuiChip-label": { px: 1 },
              "& .MuiChip-icon": { ml: 0.5, color: "#fff" },
            }}
          />
        </Stack>

        {quote?.client?.name && (
          <Chip
            icon={<Person2 sx={{ fontSize: 14 }} />}
            label={quote.client.name}
            size="small"
            variant="outlined"
            sx={{
              alignSelf: "flex-start",
              height: 24,
              bgcolor: alpha("#6b7280", 0.05),
              borderColor: alpha("var(--fx-surface-textsecondary, #64748B)", 0.2),
              color: "var(--fx-surface-textsecondary, #64748B)",
              fontSize: "0.75rem",
              fontWeight: 600,
              "& .MuiChip-label": { px: 1 },
              "& .MuiChip-icon": { color: "var(--fx-surface-textsecondary, #64748B)" },
            }}
          />
        )}

        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "1rem",
            color: "var(--fx-surface-text, #0F172A)",
            lineHeight: 1.3,
            mt: 0.5,
          }}
        >
          {title}
        </Typography>

        <Chip
          icon={<AccessTimeIcon sx={{ fontSize: 14 }} />}
          label={endTime ? `${startTime} - ${endTime}` : startTime}
          size="small"
          variant="outlined"
          sx={{
            alignSelf: "flex-start",
            height: 24,
            bgcolor: alpha("#6b7280", 0.05),
            borderColor: alpha("var(--fx-surface-textsecondary, #64748B)", 0.2),
            color: "var(--fx-surface-textsecondary, #64748B)",
            fontSize: "0.75rem",
            fontWeight: 500,
            mt: 0.4,
            "& .MuiChip-label": { px: 1 },
            "& .MuiChip-icon": { color: "var(--fx-surface-textsecondary, #64748B)" },
          }}
        />
      </Box>

      <BaseModalComponent
        defaultSizes={{ width: "600", height: "1000" }}
        modalLabel={`Job ${jobId}`}
        modalKey={`Grouped-Event-${jobId}-${event.id}`}
        elementTrigger={
          <button
            ref={triggerRef}
            type="button"
            style={{
              position: "absolute",
              left: "-99999px",
              top: "-99999px",
            }}
          />
        }
      >
        <EventJobModal arg={{ event }} />
      </BaseModalComponent>
    </>
  );
};
